import React, { useRef, useEffect } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

interface SolidityCodeViewerProps {
  code: string;
  readOnly?: boolean;
  highlightLines?: number[];
  onCodeChange?: (code: string) => void;
  height?: string;
  showLineNumbers?: boolean;
  minimap?: boolean;
}

// Solidity language configuration
const solidityLanguageConfig = {
  keywords: [
    'pragma', 'solidity', 'import', 'contract', 'interface', 'library', 'abstract',
    'function', 'modifier', 'event', 'struct', 'enum', 'mapping', 'constructor',
    'public', 'private', 'internal', 'external', 'pure', 'view', 'payable',
    'constant', 'immutable', 'virtual', 'override', 'returns', 'return',
    'if', 'else', 'for', 'while', 'do', 'break', 'continue', 'throw',
    'emit', 'require', 'assert', 'revert', 'try', 'catch',
    'new', 'delete', 'this', 'super', 'selfdestruct',
    'true', 'false', 'wei', 'gwei', 'ether', 'seconds', 'minutes', 'hours', 'days', 'weeks',
    'memory', 'storage', 'calldata', 'indexed', 'anonymous',
    'using', 'is', 'as', 'type', 'assembly', 'unchecked',
  ],
  typeKeywords: [
    'address', 'bool', 'string', 'bytes', 'byte',
    'int', 'int8', 'int16', 'int24', 'int32', 'int40', 'int48', 'int56', 'int64',
    'int72', 'int80', 'int88', 'int96', 'int104', 'int112', 'int120', 'int128',
    'int136', 'int144', 'int152', 'int160', 'int168', 'int176', 'int184', 'int192',
    'int200', 'int208', 'int216', 'int224', 'int232', 'int240', 'int248', 'int256',
    'uint', 'uint8', 'uint16', 'uint24', 'uint32', 'uint40', 'uint48', 'uint56', 'uint64',
    'uint72', 'uint80', 'uint88', 'uint96', 'uint104', 'uint112', 'uint120', 'uint128',
    'uint136', 'uint144', 'uint152', 'uint160', 'uint168', 'uint176', 'uint184', 'uint192',
    'uint200', 'uint208', 'uint216', 'uint224', 'uint232', 'uint240', 'uint248', 'uint256',
    'bytes1', 'bytes2', 'bytes3', 'bytes4', 'bytes5', 'bytes6', 'bytes7', 'bytes8',
    'bytes9', 'bytes10', 'bytes11', 'bytes12', 'bytes13', 'bytes14', 'bytes15', 'bytes16',
    'bytes17', 'bytes18', 'bytes19', 'bytes20', 'bytes21', 'bytes22', 'bytes23', 'bytes24',
    'bytes25', 'bytes26', 'bytes27', 'bytes28', 'bytes29', 'bytes30', 'bytes31', 'bytes32',
  ],
  operators: [
    '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=',
    '&&', '||', '++', '--', '+', '-', '*', '/', '&', '|', '^', '%',
    '<<', '>>', '>>>', '+=', '-=', '*=', '/=', '&=', '|=', '^=',
    '%=', '<<=', '>>=', '>>>=', '=>',
  ],
};

export const SolidityCodeViewer: React.FC<SolidityCodeViewerProps> = ({
  code,
  readOnly = true,
  highlightLines = [],
  onCodeChange,
  height = '500px',
  showLineNumbers = true,
  minimap = false,
}) => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const decorationsRef = useRef<string[]>([]);

  const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = editor;

    // Register Solidity language if not already registered
    if (!monaco.languages.getLanguages().some((lang: { id: string }) => lang.id === 'solidity')) {
      monaco.languages.register({ id: 'solidity' });

      // Set language configuration
      monaco.languages.setMonarchTokensProvider('solidity', {
        keywords: solidityLanguageConfig.keywords,
        typeKeywords: solidityLanguageConfig.typeKeywords,
        operators: solidityLanguageConfig.operators,
        symbols: /[=><!~?:&|+\-*\/\^%]+/,
        escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

        tokenizer: {
          root: [
            // Comments
            [/\/\/.*$/, 'comment'],
            [/\/\*/, 'comment', '@comment'],

            // Strings
            [/"([^"\\]|\\.)*$/, 'string.invalid'],
            [/"/, 'string', '@string'],

            // Numbers
            [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
            [/0[xX][0-9a-fA-F]+/, 'number.hex'],
            [/\d+/, 'number'],

            // Keywords
            [/[a-zA-Z_]\w*/, {
              cases: {
                '@typeKeywords': 'type',
                '@keywords': 'keyword',
                '@default': 'identifier',
              },
            }],

            // Operators
            [/@symbols/, {
              cases: {
                '@operators': 'operator',
                '@default': '',
              },
            }],

            // Delimiters
            [/[{}()\[\]]/, '@brackets'],
            [/[;,.]/, 'delimiter'],
          ],

          comment: [
            [/[^\/*]+/, 'comment'],
            [/\*\//, 'comment', '@pop'],
            [/[\/*]/, 'comment'],
          ],

          string: [
            [/[^\\"]+/, 'string'],
            [/@escapes/, 'string.escape'],
            [/\\./, 'string.escape.invalid'],
            [/"/, 'string', '@pop'],
          ],
        },
      });

      // Set language configuration for auto-closing brackets, etc.
      monaco.languages.setLanguageConfiguration('solidity', {
        comments: {
          lineComment: '//',
          blockComment: ['/*', '*/'],
        },
        brackets: [
          ['{', '}'],
          ['[', ']'],
          ['(', ')'],
        ],
        autoClosingPairs: [
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' },
          { open: "'", close: "'" },
        ],
        surroundingPairs: [
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' },
          { open: "'", close: "'" },
        ],
      });
    }

    // Apply line highlights
    if (highlightLines.length > 0) {
      applyHighlights(editor, highlightLines);
    }
  };

  const applyHighlights = (editor: editor.IStandaloneCodeEditor, lines: number[]) => {
    const decorations = lines.map(line => ({
      range: {
        startLineNumber: line,
        startColumn: 1,
        endLineNumber: line,
        endColumn: 1,
      },
      options: {
        isWholeLine: true,
        className: 'highlighted-line',
        glyphMarginClassName: 'highlighted-glyph',
      },
    }));

    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, decorations);
  };

  useEffect(() => {
    if (editorRef.current && highlightLines.length > 0) {
      applyHighlights(editorRef.current, highlightLines);
    }
  }, [highlightLines]);

  const handleChange = (value: string | undefined) => {
    if (onCodeChange && value !== undefined) {
      onCodeChange(value);
    }
  };

  return (
    <div className="rounded-lg overflow-hidden border border-surface-700">
      <Editor
        height={height}
        language="solidity"
        value={code}
        theme="vs-dark"
        onChange={handleChange}
        onMount={handleEditorDidMount}
        options={{
          readOnly,
          lineNumbers: showLineNumbers ? 'on' : 'off',
          minimap: { enabled: minimap },
          scrollBeyondLastLine: false,
          fontSize: 13,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
          tabSize: 4,
          wordWrap: 'on',
          automaticLayout: true,
          folding: true,
          foldingStrategy: 'indentation',
          renderLineHighlight: 'all',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
          padding: {
            top: 10,
            bottom: 10,
          },
        }}
      />
    </div>
  );
};

export default SolidityCodeViewer;
